import { UserCheck } from "lucide-react";
import { ROTULO_DO_ELEMENTO, type EstadoDaSala, type Planta } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { CLASSES_DO_TOM, descrever, quem } from "../estado-na-tela";
import type { SalaAgora } from "../use-salas-agora";
import { GradeDaPlanta, posicao } from "./GradeDaPlanta";

type Props = {
  planta: Planta;
  salas: SalaAgora[];
  porSala: Record<string, EstadoDaSala>;
  onAbrir?: (consultaId: string) => void;
  /** Recepção: letra maior, pra ler de longe. */
  grande?: boolean;
  alturaMaxima?: string;
};

/** A maquete com cada sala pintada pelo que acontece nela agora. */
export function PlantaAoVivo({ planta, salas, porSala, onAbrir, grande = false, alturaMaxima }: Props) {
  return (
    <GradeDaPlanta colunas={planta.colunas} linhas={planta.linhas} alturaMaxima={alturaMaxima}>
      {planta.elementos.map((e) => (
        <div
          key={e.id}
          className="absolute flex items-center justify-center p-0.5"
          style={posicao(e, planta.colunas, planta.linhas)}
        >
          <span className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-zinc-100 px-1 text-center text-[11px] font-medium text-zinc-500 dark:border-border dark:bg-muted/40 dark:text-muted-foreground">
            {e.rotulo || ROTULO_DO_ELEMENTO[e.tipo]}
          </span>
        </div>
      ))}
      {planta.salas.map((p) => {
        const sala = salas.find((s) => s.id === p.salaId);
        const estado = porSala[p.salaId];
        if (!sala || !estado) return null;
        const d = descrever(estado);
        const abrivel = !!d.consulta?.pacienteNome && !!onAbrir;
        return (
          <div key={p.salaId} className="absolute p-0.5" style={posicao(p, planta.colunas, planta.linhas)}>
            <button
              type="button"
              disabled={!abrivel}
              onClick={() => d.consulta && onAbrir?.(d.consulta.id)}
              title={[sala.nome, d.rotulo, d.consulta && quem(d.consulta), d.horario].filter(Boolean).join(" · ")}
              className={cn(
                "flex h-full w-full flex-col overflow-hidden rounded-lg border-2 p-2 text-left",
                CLASSES_DO_TOM[d.tom].caixa,
                !abrivel && "cursor-default",
              )}
            >
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: sala.cor ?? "#a1a1aa" }} />
                <span className={cn("truncate font-semibold text-zinc-900 dark:text-foreground", grande ? "text-base" : "text-xs")}>{sala.nome}</span>
              </span>
              <span className={cn("mt-1 w-fit rounded px-1.5 py-px font-bold uppercase tracking-wide", grande ? "text-[11px]" : "text-[9px]", CLASSES_DO_TOM[d.tom].selo)}>
                {d.rotulo}
              </span>
              {d.consulta && (
                <span className={cn("mt-1 truncate font-medium text-zinc-800 dark:text-foreground", grande ? "text-lg" : "text-[13px]")}>{quem(d.consulta)}</span>
              )}
              <span className={cn("truncate text-zinc-500 dark:text-muted-foreground", grande ? "text-sm" : "text-[11px]")}>
                {[d.dentista, d.horario].filter(Boolean).join(" · ")}
              </span>
              {d.chegou && (
                <span className={cn("mt-auto inline-flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-400", grande ? "text-sm" : "text-[11px]")}>
                  <UserCheck className="h-3 w-3" />
                  Chegou
                </span>
              )}
            </button>
          </div>
        );
      })}
    </GradeDaPlanta>
  );
}
